import { Link, useLocation } from "react-router-dom";
import { FaChevronRight, FaHome } from "react-icons/fa";
import "./Breadcrumb.css";

/**
 * Derives breadcrumb crumbs from the current pathname.
 * Each crumb: { label, to? }
 *   - if `to` is present  → rendered as a <Link>
 *   - last crumb (current page) → plain text, never a link
 */
const resolveCrumbs = (pathname) => {
  // Normalise — strip trailing slash
  const p = pathname.endsWith("/") && pathname !== "/"
    ? pathname.slice(0, -1)
    : pathname;

  const segments = p.split("/").filter(Boolean);
  const base     = "/" + (segments[0] || "");
  const hasId    = segments.length > 1; // e.g. /jobOrderForm/uuid-here

  const MAP = {
    "/dashboard":         [{ label: "Home" }],

    /* ── Invoicing ── */
    "/invoiceGrid":       [{ label: "Invoices" }],
    "/invoice":           [{ label: "Invoices",         to: "/invoiceGrid"  }, { label: "Create Invoice"    }],
    "/editInvoice":       [{ label: "Invoices",         to: "/invoiceGrid"  }, { label: "Edit Invoice"      }],
    "/pdfInvoice":        [{ label: "Invoices",         to: "/invoiceGrid"  }, { label: "PDF Preview"       }],

    /* ── Quotations ── */
    "/quotations":        [{ label: "Quotations" }],
    "/quotationForm":     [{ label: "Quotations",       to: "/quotations"   }, { label: hasId ? "Edit Quotation"   : "New Quotation"  }],

    /* ── Job Orders ── */
    "/jobOrders":         [{ label: "Job Orders" }],
    "/jobOrderForm":      [{ label: "Job Orders",       to: "/jobOrders"    }, { label: hasId ? "Edit Job Order"   : "New Job Order"  }],

    /* ── Dispatch ── */
    "/dispatches":        [{ label: "Dispatch Tracker" }],
    "/dispatchForm":      [{ label: "Dispatch Tracker", to: "/dispatches"   }, { label: hasId ? "Edit Dispatch"    : "New Dispatch"   }],

    /* ── Challans ── */
    "/challans":          [{ label: "Challan Register" }],
    "/challanForm":       [{ label: "Challan Register", to: "/challans"     }, { label: hasId ? "Edit Challan"     : "New Challan"    }],

    /* ── Inventory ── */
    "/rawMaterials":      [{ label: "Inventory",        },                    { label: "Raw Materials"     }],
    "/rawMaterialForm":   [{ label: "Raw Materials",    to: "/rawMaterials" }, { label: hasId ? "Edit Material"    : "New Material"   }],

    /* ── Finance ── */
    "/expenses":          [{ label: "Finance"           },                    { label: "Expenses"          }],

    /* ── Products ── */
    "/productManagement": [{ label: "Product Management" }],

    /* ── Reports ── */
    "/gstReports":        [{ label: "Reports"           },                    { label: "GST Reports"       }],
    "/paymentTracker":    [{ label: "Reports"           },                    { label: "Payment Tracker"   }],
    "/customerStatement": [{ label: "Reports"           },                    { label: "Customer Statement"}],

    /* ── Consignee Details ── */
    "/cdGrid":            [{ label: "Consignee Details" }],
    "/cdForm":            [{ label: "Consignee Details", to: "/cdGrid"      }, { label: hasId ? "Edit Consignee"   : "New Consignee"  }],

    /* ── Users ── */
    "/users":             [{ label: "User Management" }],
    "/userForm":          [{ label: "User Management",  to: "/users"        }, { label: hasId ? "Edit User"        : "New User"       }],
  };

  return MAP[base] || [];
};

const Breadcrumb = () => {
  const { pathname } = useLocation();
  const crumbs = resolveCrumbs(pathname);

  if (!crumbs.length) return null;

  return (
    <nav className="bc-bar" aria-label="Breadcrumb">
      <ol className="bc-list">
        {/* Always start with a Home icon link */}
        <li className="bc-item">
          <Link to="/dashboard" className="bc-link bc-home" title="Dashboard">
            <FaHome />
          </Link>
        </li>

        {crumbs.map((crumb, i) => {
          const isLast = i === crumbs.length - 1;
          return (
            <li key={i} className="bc-item">
              <FaChevronRight className="bc-sep" aria-hidden="true" />
              {isLast || !crumb.to ? (
                <span className={`bc-label${isLast ? " bc-current" : ""}`}>
                  {crumb.label}
                </span>
              ) : (
                <Link to={crumb.to} className="bc-link">
                  {crumb.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
